// src/types/capsule.ts

/**
 * Layer names in progression order
 */
export type LayerName =
  | 'intro'
  | 'chords'
  | 'melody'
  | 'melodyBass'
  | 'beginners'
  | 'intermediate'
  | 'advanced';

/**
 * Ordered array of layer names for UI display
 */
export const LAYER_ORDER: LayerName[] = [
  'intro',
  'chords',
  'melody',
  'melodyBass',
  'beginners',
  'intermediate',
  'advanced',
];

/**
 * Human-readable labels for layers
 */
export const LAYER_LABELS: Record<LayerName, string> = {
  intro: 'Intro',
  chords: 'Chords',
  melody: 'Melody',
  melodyBass: 'Melody + Bass',
  beginners: 'Beginners',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

/**
 * Content for a single layer within a capsule
 */
export interface LayerContent {
  /** Notation file URL or path, e.g., "melody.xml" or full Blob URL */
  file: string;
  /** Original uploaded filename (preserved for display) */
  originalFileName?: string;
  /** Optional text description (used by intro layer) */
  description?: string;
  /** Optional audio recording URL or path */
  audio?: string;
  /** Original uploaded audio filename (preserved for display) */
  originalAudioFileName?: string;
  /** BPM for audio playback */
  tempo?: number;
  /** Milliseconds offset for audio sync alignment */
  audioOffset?: number;
  /** Optional video recording URL or path (replaces audio) */
  video?: string;
  /** Original uploaded video filename (preserved for display) */
  originalVideoFileName?: string;
  /** Milliseconds offset for video sync alignment */
  videoOffset?: number;
  /** Optional zoom regions for video (percentages 0-100) */
  zoomRegions?: { rightHand?: ZoomRegion; leftHand?: ZoomRegion; };
}

/**
 * Zoom region for video (percentages 0-100)
 */
export interface ZoomRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Default zoom regions for guitar videos
 */
export const DEFAULT_ZOOM_REGIONS = {
  rightHand: { x: 0, y: 0, width: 50, height: 100 },   // Left half of video
  leftHand: { x: 50, y: 0, width: 50, height: 100 },   // Right half of video
};

/**
 * Metadata for a capsule
 */
export interface CapsuleMetadata {
  title: string;
  artist?: string;
  composer?: string;
  arranger?: string;
  difficulty?: 1 | 2 | 3 | 4 | 5;
  tags?: string[];
  notes?: string;
}

/**
 * Complete capsule definition
 */
export interface Capsule {
  /** Unique identifier */
  id: string;
  /** Folder name in scores_library, e.g., "ani-guitara" */
  slug: string;
  /** Layer content - only populated layers are present */
  layers: Partial<Record<LayerName, LayerContent>>;
  /** Capsule metadata */
  metadata: CapsuleMetadata;
  /** ISO date string */
  createdAt: string;
  /** ISO date string */
  updatedAt: string;
}

/**
 * Manifest file structure
 */
export interface CapsuleManifest {
  capsules: Capsule[];
  totalCount: number;
  generatedAt: string;
}

/**
 * Legacy score format (for backward compatibility)
 */
export interface LegacyScore {
  filename: string;
  artist: string;
  title: string;
}

/**
 * Legacy manifest format
 */
export interface LegacyManifest {
  scores: LegacyScore[];
  totalCount: number;
  generatedAt: string;
}

/**
 * Helper to get available layers from a capsule
 */
export function getAvailableLayers(capsule: Capsule): LayerName[] {
  return LAYER_ORDER.filter(layer => capsule.layers[layer] !== undefined);
}

/**
 * Check if a path is already a full URL (e.g., Blob storage URL)
 */
function isFullUrl(path: string): boolean {
  return path.startsWith('http://') || path.startsWith('https://');
}

/**
 * Helper to get layer file path
 *
 * Returns the file URL directly if it's already a full URL (Blob storage),
 * otherwise constructs a local path.
 */
export function getLayerFilePath(capsule: Capsule, layer: LayerName): string | null {
  const content = capsule.layers[layer];
  if (!content) return null;

  // If file is already a full URL (Blob storage), return it directly
  if (isFullUrl(content.file)) {
    return content.file;
  }

  // Otherwise, construct local path
  return `/scores_library/${capsule.slug}/${content.file}`;
}

/**
 * Helper to get layer audio path
 *
 * Returns the audio URL directly if it's already a full URL (Blob storage),
 * otherwise constructs a local path.
 */
export function getLayerAudioPath(capsule: Capsule, layer: LayerName): string | null {
  const content = capsule.layers[layer];
  if (!content?.audio) return null;

  // If audio is already a full URL (Blob storage), return it directly
  if (isFullUrl(content.audio)) {
    return content.audio;
  }

  // Otherwise, construct local path
  return `/scores_library/${capsule.slug}/${content.audio}`;
}

/**
 * Helper to get layer video path
 *
 * Returns the video URL directly if it's already a full URL (Blob storage),
 * otherwise constructs a local path.
 */
export function getLayerVideoPath(capsule: Capsule, layer: LayerName): string | null {
  const content = capsule.layers[layer];
  if (!content?.video) return null;

  // If video is already a full URL (Blob storage), return it directly
  if (isFullUrl(content.video)) {
    return content.video;
  }

  // Otherwise, construct local path
  return `/scores_library/${capsule.slug}/${content.video}`;
}

/**
 * Check if layer has video (vs audio)
 */
export function layerHasVideo(capsule: Capsule, layer: LayerName): boolean {
  const content = capsule.layers[layer];
  return Boolean(content?.video);
}
